import { useEffect, useRef } from 'react';
import { TaskType } from '@/shared/domain/taskTypes';

/** Usecase field the wizard's `form.labels` is saved to, or null for task types without a catalog. */
export const catalogFieldForTaskType = (taskType) => {
    if (taskType === TaskType.classification) return 'manually_added_labels';
    if (taskType === TaskType.NER) return 'manually_added_entities';
    return null;
};

/** `labels` plus the entries of `stored` it lacks (in stored order); the same array when nothing is missing. */
export const mergeCatalogLabels = (labels, stored) => {
    const current = Array.isArray(labels) ? labels : [];
    const missing = (Array.isArray(stored) ? stored : []).filter((label) => !current.includes(label));
    return missing.length ? [...current, ...missing] : labels;
};

/** The server has finished ingesting this card's rows (whatever follows — label generation, dismissal). */
const processingDone = (entry) =>
    Boolean(entry?.isCompleted || entry?.isError) ||
    entry?.phase === 'completed' ||
    entry?.phase === 'label_generation' ||
    (typeof entry?.processingProgress === 'number' && entry.processingProgress >= 100);

/** True while an upload sent with `sync_task_catalog_from_file` for this task is still being ingested. */
export const hasPendingCatalogSync = (progressStatus, usecaseId) => {
    if (!progressStatus || !usecaseId) return false;
    const wanted = String(usecaseId);
    return Object.values(progressStatus).some(
        (entry) =>
            entry?.syncTaskCatalogFromFile &&
            entry.usecaseId != null &&
            String(entry.usecaseId) === wanted &&
            !processingDone(entry)
    );
};

/**
 * Creation wizard: every labelled file upload asks the data processor to add
 * the labels the browser never saw (a file larger than the 20 MB preview) to
 * the task once the file is ingested (`sync_task_catalog_from_file`). When
 * that ingest finishes, re-read the task and add those labels to `form.labels`,
 * so the next save of the form (Business Goals, Data Collection Next, exit)
 * does not overwrite them. Lives on the wizard page because the Data
 * Collection step unmounts on Next while uploads keep running.
 */
export function useTaskCatalogSync({ usecaseId, taskType, progressStatus, refreshUsecases, setForm }) {
    const pending = hasPendingCatalogSync(progressStatus, usecaseId);
    const wasPending = useRef(false);
    useEffect(() => {
        if (pending) {
            wasPending.current = true;
            return;
        }
        if (!wasPending.current) return;
        wasPending.current = false;
        const field = catalogFieldForTaskType(taskType);
        if (!field || !usecaseId || typeof refreshUsecases !== 'function') return;
        refreshUsecases(
            (usecases) => {
                const fresh = (usecases || []).find((item) => String(item?._id) === String(usecaseId));
                if (!fresh) return;
                setForm((prev) => {
                    const labels = mergeCatalogLabels(prev.labels, fresh[field]);
                    return labels === prev.labels ? prev : { ...prev, labels };
                });
            },
            { background: true }
        );
    }, [pending, usecaseId, taskType, refreshUsecases, setForm]);
}
